import { FieldValues, UseFormReturn, useWatch } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
// import { DatePickerElement } from 'react-hook-form-mui/date-pickers';
import * as Yup from 'yup';
import { Stack, Typography } from '@mui/material';
import Avatar from '@mui/material/Avatar';

import {
    EditButtons,
    ImageField,
    MaskedTextFieldElement,
    TripleColumnLayout,
} from '@barboxx/admin-components';
import { Customer } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import {
    GENDERS,
    GenderTypes,
    ImageKitCustomMetadataKeys,
    ImageKitFolders,
    ImageKitNamedTransforms,
    ImageKitTypes,
    ImageKitUserTypes,
    MINIMUM_TEXT,
    PHONE_REGEX,
    TINY_TEXT,
    WEB_AVATAR_SIZE,
} from '@barboxx/types';
import { enumToList, getInitials, transformEmptyToNull } from '@barboxx/utils';

import { REACT_APP_ENV } from '../../../../environments/environment';

export const CUSTOMER_SCHEMA: Yup.ObjectSchema<Customer.UpdateRequest> =
    Yup.object({
        id: Yup.number(),
        firstName: Yup.string()
            .min(MINIMUM_TEXT, 'Too Short!')
            .max(TINY_TEXT, 'Too Long!')
            .required('Required'),
        lastName: Yup.string()
            .min(MINIMUM_TEXT, 'Too Short!')
            .max(TINY_TEXT, 'Too Long!')
            .required('Required'),
        displayName: Yup.string()
            .min(MINIMUM_TEXT, 'Too Short!')
            .max(TINY_TEXT, 'Too Long!')
            .transform(transformEmptyToNull)
            .nullable()
            .default(null),
        email: Yup.string().email('Must be valid email').required('Required'),
        dob: Yup.string().required('Required'),
        deviceNotificationId: Yup.string()
            .transform(transformEmptyToNull)
            .nullable()
            .default(null),
        gender: Yup.string<GenderTypes>().oneOf(enumToList(GENDERS)).required(),
        avatar: Yup.string()
            .transform(transformEmptyToNull)
            .nullable()
            .default(null),
        phoneNumber: Yup.string()
            .matches(PHONE_REGEX, 'Must be a phone number')
            .required(),
        isInternal: Yup.bool().default(false),
    });

interface Props<T extends FieldValues> {
    formContext: UseFormReturn<T>;
    onSubmit: (user: T) => void;
    id: number;
}

export function CustomerForm({
    formContext,
    onSubmit,
    id,
}: Props<Customer.UpdateRequest>) {
    const [editable, editableActions] = useBool();
    const values = useWatch(formContext);
    const initials = getInitials(values as Customer);

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <TripleColumnLayout
                LeftContent={
                    <Stack gap={2}>
                        <TextFieldElement
                            name="id"
                            disabled
                            label="Customer ID"
                        />
                        <TextFieldElement
                            required
                            name="firstName"
                            label="First Name"
                            slotProps={{
                                input: {
                                    readOnly: !editable,
                                },
                            }}
                        />
                        <TextFieldElement
                            required
                            name="lastName"
                            label="Last Name"
                            slotProps={{
                                input: {
                                    readOnly: !editable,
                                },
                            }}
                        />

                        <DatePickerElement
                            required
                            name="dob"
                            label="DOB"
                            readOnly={!editable}
                        />
                    </Stack>
                }
                CenterContent={
                    <Stack alignItems="center" flex={1}>
                        <ImageField
                            label="Avatar"
                            name="avatar"
                            readOnly={!editable}
                            imagekitUploadProps={{
                                fileName: `${id}`,
                                folder: ImageKitFolders.CustomerAvatar,
                                env: REACT_APP_ENV,

                                customMetadata: {
                                    [ImageKitCustomMetadataKeys.Id]: `${id}`,
                                    [ImageKitCustomMetadataKeys.UserType]:
                                        ImageKitUserTypes.Customer,
                                    [ImageKitCustomMetadataKeys.Type]:
                                        ImageKitTypes.Avatar,
                                    [ImageKitCustomMetadataKeys.Env]:
                                        REACT_APP_ENV,
                                },
                                tags: [
                                    `${id}`,
                                    ImageKitTypes.Avatar,
                                    ImageKitUserTypes.Customer,
                                    REACT_APP_ENV,
                                ],
                                transformation: {
                                    post: [
                                        {
                                            type: 'transformation',
                                            value: ImageKitNamedTransforms.Web
                                                .square,
                                        },
                                    ],
                                },
                            }}
                            imagekitProps={{
                                alt: 'User photo',
                                width: WEB_AVATAR_SIZE,
                                height: WEB_AVATAR_SIZE,
                                transformation: [
                                    {
                                        raw: ImageKitNamedTransforms.Web.square,
                                    },
                                    {
                                        raw: ImageKitNamedTransforms.Common
                                            .avatar,
                                    },
                                ],
                                loading: 'lazy',
                                lqip: {
                                    raw: ImageKitNamedTransforms.Web.squareLq,
                                },
                            }}
                            EmptyImageComponent={() => (
                                <Avatar
                                    sx={{
                                        width: WEB_AVATAR_SIZE,
                                        height: WEB_AVATAR_SIZE,
                                    }}
                                >
                                    {initials && (
                                        <Typography
                                            fontSize="8rem"
                                            variant="h6"
                                        >
                                            {initials}
                                        </Typography>
                                    )}
                                </Avatar>
                            )}
                        />
                    </Stack>
                }
                RightContent={
                    <Stack gap={2}>
                        <TextFieldElement
                            name="email"
                            label="Email"
                            type="email"
                            slotProps={{
                                input: {
                                    readOnly: true,
                                },
                            }}
                        />
                        <TextFieldElement
                            name="displayName"
                            label="Display Name"
                            slotProps={{
                                input: {
                                    readOnly: true,
                                },
                            }}
                        />
                        <MaskedTextFieldElement
                            name="phoneNumber"
                            slotProps={{
                                input: {
                                    readOnly: true,
                                },
                            }}
                            label="Phone Number"
                            type="tel"
                            inputProps={{
                                mask: '+1 (#00) 000-0000',
                                definitions: {
                                    '#': /[1-9]/,
                                },
                            }}
                        />
                        <CheckboxElement
                            name="over21"
                            label="Over 21"
                            disabled
                        />
                        <CheckboxElement
                            name="isInternal"
                            label="Internal"
                            disabled={!editable}
                        />
                    </Stack>
                }
            />
            <Can I="update" a="Customer">
                <EditButtons
                    formContext={formContext}
                    handleCancel={handleCancel}
                    editable={editable}
                    editableActions={editableActions}
                />
            </Can>
        </FormContainer>
    );
}
